import {ActionTree} from 'vuex'
import {AxiosError, AxiosResponse} from "axios";
import moment from 'moment';
import ICategory from "@/modules/ProjectManager/ts/ICategory";
import IProjectStats from "@/modules/ProjectManager/ts/IProjectStats";
import ISortConfig from "@/modules/ProjectManager/ts/ISortConfig";
import IProjectGeoStats from "@/modules/ProjectManager/ts/IProjectGeoStats";

// @ts-ignore
import Api from "@/api.js";

export const actions: ActionTree<any, any> = {

    loadProjects: ({state, getters, commit, dispatch}, {force}) => {

        if (force) {
            state.projectsCacheExpires = null;
        }

        return new Promise((resolve, reject) => {

            // Pull from store if loaded and cache hasn't expired
            if (state.projectsCacheExpires === null || moment().isAfter(state.projectsCacheExpires)) {

                commit('setProjectsLoading', true);

                let url = '/projects?compact=1';

                Api.get(url)

                    .then(({data}: AxiosResponse) => {

                        commit('setProjectsLoading', false);
                        commit('setProjects', data.projects);
                        resolve(null);

                    })
                    .catch((error: AxiosError) => {
                        commit('setProjectsLoading', false);
                        reject(error);
                    });
            } else {
                resolve(null);
            }
        });
    },

    loadArchivedProjects: ({state, getters, commit, dispatch}, config: ISortConfig) => {

        return new Promise((resolve, reject) => {

            let url = '/projects/archived?page=' + config.page
                + '&sortBy=' + config.sortBy
                + '&itemsPerPage=' + config.itemsPerPage
                + '&sortDesc=' + config.sortDesc;

            if (config.search && config.search.length > 0) {
                url = url + '&search=' + config.search;
            }

            Api.get(url)

                .then(({data}: AxiosResponse) => {
                    commit('setArchivedProjects', data.projects.data);
                    resolve(data.projects);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getProject: ({state, getters, commit, dispatch}, {id, force}) => {

        return new Promise((resolve, reject) => {

            // See if project is found already
            const project = getters.findProject(id);

            if (project && project.compensation && !force) {

                resolve(project);

            } else {

                Api.get('/project/' + id)

                    .then(({data}: AxiosResponse) => {
                        commit('updateProject', data.project)
                        resolve(data.project);
                    })
                    .catch((error: AxiosError) => {
                        reject(error);
                    });
            }
        });
    },

    createProject: ({state, getters, commit, dispatch}, form) => {

        return new Promise((resolve, reject) => {

            Api.post('/project', form.data)

                .then((response: AxiosResponse) => {

                    commit('setProminentProject', response.data.project);
                    commit('createProject', response.data.project);

                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    reject(error);
                });

        });
    },

    updateProject: ({state, getters, commit, dispatch}, form) => {

        return new Promise((resolve, reject) => {

            Api.put('/project',form.data)

                .then((response: AxiosResponse) => {

                    commit('setProminentProject', response.data.project);
                    commit('updateProject', response.data.project);

                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    reject(error);
                });

        });
    },

    changeProjectStatus: ({state, getters, commit, dispatch}, data) => {

        return new Promise((resolve, reject) => {

            const projectsBackup = [...state.projects];

            commit('updateProjectStatus', data);

            Api.put('/project/' + data.id + '/status', {status: data.status})

                .then((response: AxiosResponse) => {
                    commit('updateProject', response.data.project)
                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    commit('setProjects', projectsBackup);
                    reject(error);
                });

        });
    },

    deleteProject: ({state, getters, commit, dispatch}, projectId: number) => {

        return new Promise((resolve, reject) => {

            Api.delete('/project/' + projectId)

                .then((response: AxiosResponse) => {

                    if (state.prominentProject && state.prominentProject.id === projectId) {
                        commit('clearProminentProject');
                    }

                    commit('deleteProject', projectId);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });

        });
    },

    cloneProject: ({state, getters, commit, dispatch}, {projectId, cloneParticipants}) => {

        return new Promise((resolve, reject) => {

            let url = '/project/' + projectId + '/clone';

            if (cloneParticipants) {
                url += '?clone_participants=true';
            }

            Api.get(url)

                .then((response: AxiosResponse) => {

                    commit('setProminentProject', response.data.project);
                    commit('createProject', response.data.project);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });

        });
    },

    loadCategories: ({state, getters, commit, dispatch}, forceUpdate: boolean) => {

        if (forceUpdate) {
            state.categoriesCacheExpires = null;
        }

        // Pull from store if loaded and cache hasn't expired
        if (state.categoriesCacheExpires === null ||
            moment().isAfter(state.categoriesCacheExpires)) {

            commit('setCategoriesLoading', true);

            return new Promise((resolve, reject) => {

                Api.get('/categories')

                    .then(({data}: AxiosResponse) => {

                        commit('setCategories', data.categories);
                        commit('setCategoriesLoading', false);
                        resolve(null);
                    })
                    .catch((error: AxiosError) => {
                        commit('setCategoriesLoading', false);
                        reject(error);
                    });
            });
        }
    },

    createCategory: ({state, getters, commit, dispatch}, category: ICategory) => {

        // Clone original
        const categories = [...state.categories];

        commit('createCategory', category);

        return new Promise((resolve, reject) => {

            Api.post('/category', category)

                .then((response: AxiosResponse) => {

                    commit('saveCategoryId', response.data.category.id);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('setCategories', categories);

                    reject(error);
                });
        });
    },

    updateCategory: ({state, getters, commit, dispatch}, category: ICategory) => {

        // Clone original
        const categories = [...state.categories];

        commit('updateCategory', category);

        return new Promise((resolve, reject) => {

            Api.put('/category', category)

                .then((response: AxiosResponse) => {

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('setCategories', categories);

                    reject(error);
                });
        });
    },

    /**
     * Delete category
     */
    deleteCategory: ({state, getters, commit, dispatch}, category: ICategory) => {

        // Clone original
        const categories = [...state.categories];

        commit('deleteCategory', category);

        return new Promise((resolve, reject) => {

            Api.delete('/category/' + category.id)

                .then((response: AxiosResponse) => {

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('setCategories', categories);

                    reject(error);
                });
        });
    },

    /**
     * Get project invitation / response statistics by time function
     */
    getProjectStats: ({state, getters, commit, dispatch}, {forceUpdate, projectId}) => {

        return new Promise((resolve, reject) => {

            let found = null;

            if (!forceUpdate) {
                found = state.projectStats.find((stats: IProjectStats) => {
                    return stats.id === projectId;
                });
            }

            // Pull from store if loaded and cache hasn't expired
            if (!found || found.expires === null || moment().isAfter(found.expires)) {

                commit('setProjectStatsLoading', true);

                Api.get('/project/' + projectId + '/stats')

                    .then(({data}: AxiosResponse) => {

                        const projectData = {
                            id: projectId,
                            data: data.items,
                            first_recorded_activity: data.first_recorded_activity,
                            last_recorded_activity: data.last_recorded_activity,
                            average_loi_seconds: data.average_loi_seconds,
                            rewards: data.rewards,
                            revenue: data.revenue,
                            expires: moment().add(15, 'minutes'),
                        };

                        commit('setProjectStats', projectData);
                        commit('setProjectStatsLoading', false);
                        resolve(projectData);
                    })
                    .catch((error: AxiosError) => {
                        commit('setProjectStatsLoading', false);
                        reject(error);
                    });
            } else {
                resolve(found);
            }
        });
    },

    /**
     * Get project invitation / response statistics by time function
     */
    getProjectGeoStats: ({state, getters, commit, dispatch}, {forceUpdate, projectId}) => {

        return new Promise((resolve, reject) => {

            let found = null;

            if (!forceUpdate) {
                found = state.projectGeoStats.find((stats: IProjectGeoStats) => {
                    return stats.id === projectId;
                });
            }

            // Pull from store if loaded and cache hasn't expired
            if (!found || moment().isAfter(found.expires)) {

                commit('setProjectGeoStatsLoading', true);

                Api.get('/project/' + projectId + '/geo-stats')

                    .then(({data}: AxiosResponse) => {

                        const projectData = {
                            id: projectId,
                            data: data.data,
                            expires: moment().add(15, 'minutes'),
                        };

                        commit('setProjectGeoStats', projectData);
                        commit('setProjectGeoStatsLoading', false);
                        resolve(data);
                    })
                    .catch((error: AxiosError) => {
                        commit('setProjectGeoStatsLoading', false);
                        reject(error);
                    });
            } else {
                resolve(found);
            }
        });
    },

    getEntryTestLink: ({state, getters, commit, dispatch}, {projectId, subPanelId, locale, mode}) => {

        return new Promise((resolve, reject) => {

            Api.get('/project/' + projectId + '/entry-test-link/' + subPanelId + '/' + locale + '?mode=' + mode)

                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    sendTestEmail: ({state, getters, commit, dispatch}, data) => {

        return new Promise((resolve, reject) => {

            Api.post('/test-email', data)

                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getProjectErrors: ({state, getters, commit, dispatch}, projectId: number) => {

        return new Promise((resolve, reject) => {

            Api.get('/project/' + projectId + '/errors')

                .then((response: AxiosResponse) => {
                    commit('setProjectErrors', response.data.errors);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    deleteProjectErrors: ({state, getters, commit, dispatch}, projectId: number) => {

        return new Promise((resolve, reject) => {

            commit('deleteProjectErrors', projectId);

            Api.delete('/project/' + projectId + '/errors')

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    loadParticipants: ({state, getters, commit, dispatch}, {project_id, cursor, filter, items_per_page}) => {

        return new Promise((resolve, reject) => {

            let url = '/project/' + project_id + '/participants';

            let params = [];

            if (cursor) {
                params.push('cursor=' + cursor);
            }

            if (filter) {
                params.push('filter=' + filter);
            }

            if (items_per_page) {
                params.push('items_per_page=' + items_per_page)
            }

            if (params.length > 0) {
                url += "?" + params.join('&');
            }

            Api.get(url)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    reconcileParticipants: ({state, getters, commit, dispatch}, {projectId, reason, mode, statuses, identifiers, commitChanges}) => {

        return new Promise((resolve, reject) => {

            Api.post('/project/' + projectId + '/reconcile', {
                identifiers: identifiers,
                mode: mode,
                reason: reason,
                statuses: statuses,
                commit: commitChanges,
            })
                .then((response: AxiosResponse) => {
                    if (response.data.data.project) {
                        commit('updateProject', response.data.data.project);
                    }
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    loadQuotas: ({state, getters, commit, dispatch}, id) => {

        return new Promise((resolve, reject) => {

            Api.get('/project/' + id + '/quotas')

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    loadFeasibility: ({state, getters, commit, dispatch}, data) => {

        return new Promise((resolve, reject) => {

            Api.post('/feasibility', data)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    toggleEnforceQuota: ({state, getters, commit, dispatch}, {enforce, projectId, quotaId}) => {

        return new Promise((resolve, reject) => {

            Api.post('/project/' + projectId + '/quota/' + quotaId, {enforce: enforce})

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    exportParticipants: ({state, getters, commit, dispatch}, {status, projectId}) => {
        return new Promise((resolve, reject) => {

            Api.post('/project/' + projectId + '/export-participants', {status: status})

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    setQualitativeStatus: ({state, getters, commit, dispatch}, {projectId, status, panelistId}) => {

        return new Promise((resolve, reject) => {

            const data = {
                status: status
            }

            Api.post('/project/' + projectId + '/qualitative-participant/' + panelistId + '/status', data)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    uploadToGallery: ({state, getters, commit, dispatch}, formData) => {

        return new Promise((resolve, reject) => {

            Api.post('/project/gallery', formData)

                .then((response: AxiosResponse) => {
                    commit('insertToGallery', response.data.file)
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error.response);
                });
        });
    },

    loadGalleryImageUrls: ({state, getters, commit, dispatch}, formData) => {

        return new Promise((resolve, reject) => {

            if (state.galleryCacheExpires === null || moment().isAfter(state.galleryCacheExpires)) {

                Api.get('/project/gallery', formData)

                    .then((response: AxiosResponse) => {
                        commit('setGallery', response.data.files);
                        resolve(response);
                    })
                    .catch((error: AxiosError) => {
                        reject(error.response);
                    });

            } else {

                resolve({data: {files: state.gallery}});
            }
        });
    },

    deleteGalleryItem: ({state, getters, commit, dispatch}, imageUrl) => {

        return new Promise((resolve, reject) => {

            const imageName = imageUrl.split('/').pop();

            Api.post('/project/gallery/trash', {file: imageName})

                .then((response: AxiosResponse) => {
                    commit('deleteGalleryItem', imageUrl);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    verifyIdentifiers: ({state, getters, commit, dispatch}, {panelistIds, passcodes}) => {

        return new Promise((resolve, reject) => {

            Api.post('/project/re-contact-participants/identifiers', {
                panelist_ids: panelistIds,
                passcodes: passcodes,
            })

                .then((response: AxiosResponse) => {
                    commit('appendRecontactParticipants', response.data.data.panelists);
                    commit('appendSubPanelIds', response.data.data.sub_panel_ids);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    verifyDataVariableData: ({state, getters, commit, dispatch}, {dataVariableId, dataVariableValues, passcodes}) => {

        return new Promise((resolve, reject) => {

            Api.post('/project/re-contact-participants/data-variable-values', {
                data_variable_id: dataVariableId,
                data_variable_values: dataVariableValues,
                passcodes: passcodes,
            })

                .then((response: AxiosResponse) => {
                    commit('appendRecontactParticipants', response.data.data.panelists);
                    commit('appendSubPanelIds', response.data.data.sub_panel_ids);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

   getRecontactParticipants: ({state, getters, commit, dispatch}, projectId) => {

        return new Promise((resolve, reject) => {

            Api.get('/project/' + projectId  + "/re-contact-participants")

                .then((response: AxiosResponse) => {
                    commit('setRecontactParticipants', response.data.data.panelists);
                    commit('setSelectedSubPanelIds', response.data.data.sub_panel_ids);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    resendInvitation: ({state, getters, commit, dispatch}, {panelistId, projectId, type}) => {

        return new Promise((resolve, reject) => {

            const data = {
                panelist_id: panelistId,
                type: type
            };

            Api.post('/project/' + projectId + '/resend', data)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    exportProjects: ({state, getters, commit, dispatch}, config) => {

        return new Promise((resolve, reject) => {

            Api.post('/projects/export', config)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    deleteSecurityEvents: ({state, getters, commit, dispatch}, projectId) => {

        return new Promise((resolve, reject) => {

            Api.delete('/project/' + projectId  + "/security-events")

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },
}
